import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { DocumentItem } from "@/components/DocumentListContainer/DocumentListContainer.types";
import type { LoanApplicationData } from "../hooks/useNbiForm";
import { useNbiFormContextSafe } from "./NbiFormContext";
import { useDocumentPolling } from "../hooks/useDocumentPolling";
import { useRegion } from "@/config/RegionContext";

interface DocumentCountContextType {
    completedCount: number;
    totalCount: number;
    updateCounts: () => void;
}

export const DocumentCountContext = createContext<DocumentCountContextType | undefined>(undefined);

interface DocumentCountProviderProps {
    children: ReactNode;
}

export const DocumentCountProvider = ({ children }: DocumentCountProviderProps) => {
    const [documentCounts, setDocumentCounts] = useState({ completedCount: 0, totalCount: 0 });
    const formContext = useNbiFormContextSafe();
    const form = formContext?.form;
    const region = useRegion();

    const calculateDocumentCounts = useCallback(() => {
        if (!form) return;

        const formData = form.state.values as LoanApplicationData;
        const uploadStates = formData.uploadStates || [];
        const storedDocuments = formData.documents || [];

        const mapUploadStateToDocumentItem = (uploadState: {
            fileName?: string;
            file?: File;
            status: string;
            error?: string;
        }): DocumentItem => ({
            fileName: uploadState.fileName || "Unknown file",
            status:
                uploadState.status === "uploading"
                    ? "uploading"
                    : uploadState.status === "processing"
                      ? "processing"
                      : uploadState.status === "success"
                        ? "processed"
                        : "failed",
            error: uploadState.error,
            id: uploadState.fileName,
        });

        const mapStoredDocumentToDocumentItem = (doc: {
            id: number;
            fileName: string;
            status: "success" | "error" | "uploading";
            uploadedAt?: string;
        }): DocumentItem => ({
            fileName: doc.fileName,
            status: "processed",
            id: `stored-${doc.id}`,
        });

        const allDocuments: DocumentItem[] = [
            ...uploadStates.map(mapUploadStateToDocumentItem),
            ...storedDocuments.map(mapStoredDocumentToDocumentItem),
        ];

        const completedCount = allDocuments.filter((doc) => doc.status === "processed").length;
        const totalCount = allDocuments.length;

        setDocumentCounts((prev) => {
            if (prev.completedCount !== completedCount || prev.totalCount !== totalCount) {
                return { completedCount, totalCount };
            }
            return prev;
        });
    }, [form?.state.values]);

    useEffect(() => {
        calculateDocumentCounts();
    }, [calculateDocumentCounts]);

    useEffect(() => {
        if (!form) return;

        const formData = form.state.values as LoanApplicationData;
        const uploadStates = formData.uploadStates || [];
        const hasActiveUploads = uploadStates.some(
            (state) => state.status === "uploading" || state.status === "processing"
        );

        if (hasActiveUploads) {
            const interval = setInterval(calculateDocumentCounts, 200);
            return () => clearInterval(interval);
        }
    }, [form?.state.values, calculateDocumentCounts]);

    const formData = form?.state.values as LoanApplicationData | undefined;
    const applicationId = formData?.applicationId;
    const uploadStates = formData?.uploadStates || [];

    useDocumentPolling({
        applicationId,
        form,
        uploadStates,
        onDocumentProcessed: calculateDocumentCounts,
        defaultCurrency: region.currencyCode,
    });

    const value = {
        completedCount: documentCounts.completedCount,
        totalCount: documentCounts.totalCount,
        updateCounts: calculateDocumentCounts,
    };

    return <DocumentCountContext.Provider value={value}>{children}</DocumentCountContext.Provider>;
};

export const useDocumentCount = () => {
    const context = useContext(DocumentCountContext);
    if (!context) {
        throw new Error("useDocumentCount must be used within a DocumentCountProvider");
    }
    return context;
};

export const useDocumentCountSafe = () => {
    return useContext(DocumentCountContext);
};
